import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { compareJobNames, siteFolderName } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import { useApiQuery } from '../api/useApiQuery';
import type { BrowseCustomer, BrowseSupplier, JobSummary } from '../api/types';
import { routes } from '../lib/links';
import { Breadcrumbs } from '../components/EntityHeader';
import { Button } from '../components/ui/Button';
import { Chip } from '../components/ui/Chip';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Icon } from '../components/ui/Icon';
import { Spinner } from '../components/ui/Spinner';

type Kind = 'customer' | 'supplier';
const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
const PAGE = 50;

function statusVariant(status: string): 'success' | 'warning' | 'info' {
  const s = status.toLowerCase();
  if (['a', 'active', 'open'].includes(s)) return 'success';
  if (['i', 'inactive', 'closed', 'archived'].includes(s)) return 'warning';
  return 'info';
}

/** Oracle status codes as words. */
function statusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === 'a') return 'Active';
  if (s === 'i') return 'Inactive';
  return status;
}

/**
 * Alphabetical tree of customers (with their jobs underneath) or suppliers, narrowed by branch and
 * first letter. For when you know roughly where a record is but not what to type.
 */
export function BrowsePage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const kind: Kind = params.get('kind') === 'supplier' ? 'supplier' : 'customer';
  const branch = params.get('branch');
  const letter = params.get('letter');

  const facets = useApiQuery((client, signal) => (kind === 'customer' ? client.browse.customerFacets(signal) : client.browse.supplierFacets(signal)), [kind]);

  const [customers, setCustomers] = useState<BrowseCustomer[]>([]);
  const [suppliers, setSuppliers] = useState<BrowseSupplier[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(
    async (after: string | null, signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        if (kind === 'customer') {
          const res = await api.browse.customers({ branch, letter, cursor: after, limit: PAGE }, signal);
          if (signal?.aborted) return;
          setCustomers((prev) => (after ? [...prev, ...res.customers] : res.customers));
          setCursor(res.nextCursor);
        } else {
          const res = await api.browse.suppliers({ letter, cursor: after, limit: PAGE }, signal);
          if (signal?.aborted) return;
          setSuppliers((prev) => (after ? [...prev, ...res.suppliers] : res.suppliers));
          setCursor(res.nextCursor);
        }
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [api, kind, branch, letter],
  );

  useEffect(() => {
    const controller = new AbortController();
    setCustomers([]);
    setSuppliers([]);
    setCursor(null);
    void load(null, controller.signal);
    return () => controller.abort();
  }, [load]);

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };

  const letterCounts = useMemo(() => new Map((facets.data?.letters ?? []).map((l) => [l.key ?? '#', l.count])), [facets.data]);
  const total = useMemo(() => (facets.data?.letters ?? []).reduce((n, l) => n + l.count, 0), [facets.data]);
  const shown = kind === 'customer' ? customers.length : suppliers.length;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Breadcrumbs items={[{ label: 'Browse' }]} />
          <h1 className="mt-2 text-4xl leading-none">Browse</h1>
        </div>
        <div className="gsl-seg" role="group" aria-label="What to browse">
          <button type="button" aria-pressed={kind === 'customer'} onClick={() => set({ kind: null, branch: null, letter: null })}>
            <Icon name="building" size={14} /> Customers
          </button>
          <button type="button" aria-pressed={kind === 'supplier'} onClick={() => set({ kind: 'supplier', branch: null, letter: null })}>
            <Icon name="truck" size={14} /> Suppliers
          </button>
        </div>
      </div>

      <div className="gsl-letters" role="group" aria-label="First letter">
        <button type="button" aria-pressed={!letter} onClick={() => set({ letter: null })}>
          All
        </button>
        {LETTERS.map((l) => {
          const n = letterCounts.get(l) ?? 0;
          return (
            <button key={l} type="button" aria-pressed={letter === l} disabled={facets.data !== null && n === 0} title={n ? `${n}` : 'None'} onClick={() => set({ letter: l })}>
              {l}
            </button>
          );
        })}
      </div>

      <div className={kind === 'customer' ? 'grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)]' : ''}>
        {kind === 'customer' ? (
          <nav className="gsl-panel self-start p-2" aria-label="Branches">
            <p className="px-2 pb-1 pt-1 text-xs font-medium text-muted-foreground">Branch</p>
            <button type="button" className="gsl-tree-item" aria-current={!branch ? 'true' : undefined} onClick={() => set({ branch: null })}>
              <Icon name="building" size={15} className="text-muted-foreground" />
              <span className="truncate">All branches</span>
              <span className="gsl-num ml-auto text-xs text-muted-foreground">{facets.data ? total : ''}</span>
            </button>
            {(facets.data?.branches ?? []).map((b) => (
              <button key={b.key ?? '—'} type="button" className="gsl-tree-item" aria-current={branch === (b.key ?? '') ? 'true' : undefined} onClick={() => set({ branch: b.key ?? null })}>
                <Icon name="folder" size={15} className="text-muted-foreground" />
                <span className="truncate">{b.key ?? 'No branch'}</span>
                <span className="gsl-num ml-auto text-xs text-muted-foreground">{b.count}</span>
              </button>
            ))}
          </nav>
        ) : null}

        <section className="gsl-panel overflow-hidden" aria-label={kind === 'customer' ? 'Customers' : 'Suppliers'}>
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-xs text-muted-foreground">
            <span>
              {kind === 'customer' ? 'Customers' : 'Suppliers'}
              {branch ? ` in ${branch}` : ''}
              {letter ? ` starting with ${letter}` : ''}
            </span>
            <span className="gsl-num">
              {shown}
              {cursor ? '+' : ''} shown
            </span>
          </div>
          {error ? (
            <div className="p-4">
              <ErrorBanner title="Could not load the list" error={error} />
            </div>
          ) : null}
          {kind === 'customer' ? (
            <ul className="divide-y divide-border">
              {customers.map((c) => (
                <CustomerNode key={c.id} customer={c} />
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-border">
              {suppliers.map((s) => (
                <li key={s.id}>
                  <Link to={routes.supplier(s.supplierNumber)} className="gsl-browse-row">
                    <Icon name="truck" size={16} className="text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{s.name}</span>
                    <span className="gsl-num text-xs text-muted-foreground">{s.supplierNumber}</span>
                    <Chip variant={statusVariant(s.status)}>{statusLabel(s.status)}</Chip>
                    <Icon name="chevron-right" size={15} className="text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {loading ? (
            <div className="p-4">
              <Spinner label="Loading" />
            </div>
          ) : shown === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Nothing here yet.</p>
          ) : null}
          {cursor && !loading ? (
            <div className="border-t border-border p-3 text-center">
              <Button variant="secondary" size="sm" onClick={() => void load(cursor)}>
                Show more
              </Button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function CustomerNode({ customer: c }: { customer: BrowseCustomer }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState<JobSummary[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const expandable = c.openJobs > 0;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && jobs === null) {
      try {
        const res = await api.customers.jobs(c.accountNumber, 'all');
        // Misc, then Holding, then the rest naturally: the same order as the folder tree.
        setJobs([...res.jobs].sort((a, b) => compareJobNames(siteFolderName(a.jobReference, a.partySiteNumber, a.siteId), siteFolderName(b.jobReference, b.partySiteNumber, b.siteId))));
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  };

  return (
    <li>
      <div className="gsl-browse-row">
        {expandable ? (
          <button
            type="button"
            className="gsl-btn gsl-btn-ghost gsl-btn-sm gsl-btn-icon"
            aria-expanded={open}
            aria-label={open ? `Collapse ${c.name}` : `Expand ${c.name}`}
            onClick={() => void toggle()}
          >
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
          </button>
        ) : (
          <span className="inline-block w-[26px]" aria-hidden="true" />
        )}
        <Icon name={open ? 'folder-open' : 'folder'} size={16} className="text-muted-foreground" />
        <Link to={routes.customer(c.accountNumber)} className="min-w-0 flex-1 truncate text-sm font-medium no-underline hover:text-accent-foreground">
          {c.name}
        </Link>
        <span className="gsl-num text-xs text-muted-foreground">{c.accountNumber}</span>
        {c.branchCode ? <Chip variant="neutral">{c.branchCode}</Chip> : null}
        <Chip variant={statusVariant(c.status)}>{statusLabel(c.status)}</Chip>
        <span className="gsl-num w-16 text-right text-xs text-muted-foreground">{c.openJobs > 0 ? `${c.openJobs} job${c.openJobs === 1 ? '' : 's'}` : ''}</span>
        <Link to={routes.customer(c.accountNumber)} className="gsl-btn gsl-btn-ghost gsl-btn-sm gsl-btn-icon" aria-label={`Open ${c.name}`} title="Open">
          <Icon name="chevron-right" size={15} />
        </Link>
      </div>
      {open ? (
        <ul className="border-t border-border bg-surface-raised">
          {jobs === null && !error ? (
            <li className="px-4 py-2 pl-14">
              <Spinner label="Loading jobs" />
            </li>
          ) : error ? (
            <li className="px-4 py-2 pl-14">
              <ErrorBanner error={error} />
            </li>
          ) : jobs && jobs.length === 0 ? (
            <li className="px-4 py-2 pl-14 text-xs text-muted-foreground">No jobs on this customer.</li>
          ) : (
            jobs?.map((j) => (
              <li key={j.siteId}>
                <Link to={routes.job(j.siteId)} className="gsl-browse-row pl-14">
                  <Icon name="briefcase" size={15} className="text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm">{siteFolderName(j.jobReference, j.partySiteNumber, j.siteId)}</span>
                  {j.address ? <span className="hidden truncate text-xs text-muted-foreground md:inline">{j.address}</span> : null}
                  {j.archivedAt ? <Chip variant="neutral">Archived</Chip> : <Chip variant="success">Open</Chip>}
                  <Icon name="chevron-right" size={15} className="text-muted-foreground" />
                </Link>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </li>
  );
}
